import { PlatformActions, NativeActions, UserInfo, ImageInfo, FileSelection, ScreenshotCropConfig, TrackingIdInfo, Accounts, LocalServerActions } from "./PlatformActions";
import { NativeCommChannel, Command, PlatformEventHandler, PlatformEvent } from "./NativeCommChannel";
import { VSInstanceInfo } from "../store/appState";
import { SettingsUpdate } from "../SettingsUpdate";
import { Settings } from "../Settings";
import { stringFromErrorOrAny } from "../utils/tools";
import { FileUploadConfig } from "../DataCollectorConfiguration";
import { App } from "../app";
import { TelemetryResult } from "../telemetry/TelemetryHelper";

export class WebSocketActions implements PlatformActions, NativeActions {
    private channel: NativeCommChannel;
    private connectPromise: Promise<any>;

    constructor(platformEventHandler: PlatformEventHandler, info: ConnectionInfo) {
        const channel: NativeCommChannel = new NativeCommChannel();
        this.connectPromise = channel.connect(platformEventHandler, info);

        this.channel = channel;

        this.hello(info.fsid).then((settings: Settings) => {
            console.log("HELLO response");
            console.log(settings);

            platformEventHandler(PlatformEvent.serverSettings, settings);

            App.telemetry.postOperation(
                "HelloResponse",
                TelemetryResult.Success,
                null,
                {
                    loadStartMsec: settings.loadStartMsec,
                    loadDurationMsec: settings.loadDurationMsec
                }
            );
        }).catch((reason: any) => {
            console.error(stringFromErrorOrAny(reason));
            platformEventHandler(PlatformEvent.clientError, { errorMessage: stringFromErrorOrAny(reason) });
        });
    }

    private async hello(fsid: string): Promise<Settings> {
        await this.connectPromise;
        console.log(`********* <HELLO: ${fsid} /> *********`)
        return this.channel.send(Command.hello, { fsid });
    }

    public async get(authRequired: boolean, partialUri: string): Promise<any> {
        await this.connectPromise;
        console.log(`********* <GET: ${partialUri}, authRequired: ${authRequired} /> *********`)
        return this.channel.send(Command.get, { partialUri, authRequired });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    public async post(authRequired: boolean, partialUri: string, data: any): Promise<any> {
        await this.connectPromise;
        console.log(`********* <POST: ${partialUri}, authRequired: ${authRequired}> *********`)
        console.log(data);
        console.log(`************************* </POST> *************************`)
        return this.channel.send(Command.post, { partialUri, data: JSON.stringify(data), authRequired });
    }

    public async signOutAndIn(): Promise<UserInfo> {
        await this.connectPromise;
        console.log(`********* <SIGN_OUT_AND_IN /> *********`)
        return this.channel.send(Command.signOutAndIn, {});
    }

    public async signOut(): Promise<void> {
        await this.connectPromise;
        console.log(`********* <SIGN_OUT /> *********`)
        return this.channel.send(Command.signOut, {});
    }

    public async checkForSignIn(): Promise<Accounts | UserInfo | null> {
        await this.connectPromise;
        console.log(`********* <CHECK_FOR_SIGN_IN /> *********`)
        return this.channel.send(Command.checkForSignIn, {});
    }

    getNativeActions(): NativeActions {
        return this;
    }

    getLocalServerActions(): null {
        return null;
    }

    public async newVsInstance(): Promise<void> {
        await this.connectPromise;
        console.log(`********* <NEW_VS_INSTANCE /> *********`)
        return this.channel.send(Command.startVsInstance, {});
    }

    public async getRunningVsInstances(): Promise<VSInstanceInfo[]> {
        await this.connectPromise;
        const instances: VSInstanceInfo[] = await this.channel.send(Command.getRunningVsInstances, {});
        instances.forEach((instance: VSInstanceInfo) => {
            instance.title = instance.title ?? "Visual Studio";
        });
        return instances;
    }

    public async updateSettings(vsPid: number): Promise<SettingsUpdate> {
        await this.connectPromise;
        console.log(`********* <UPDATE_SETTINGS: ${vsPid} /> *********`)
        return this.channel.send(Command.updateSettings, { vsPid });
    }

    public async startCollectDiagnostics(processId: number, additionalProcessIds: number[], diagSessionId: number): Promise<void> {
        await this.connectPromise;
        console.log(`********* <START_COLLECT_DIAGNOSTICS> *********`)
        console.log({ processId, additionalProcessIds, diagSessionId });
        console.log(`********* </START_COLLECT_DIAGNOSTICS> *********`)
        return this.channel.send(Command.startCollectDiagnostics, { processId, additionalProcessIds, diagSessionId });
    }

    public async stopCollectDiagnostics(): Promise<void> {
        await this.connectPromise;
        console.log(`********* <STOP_COLLECT_DIAGNOSTICS /> *********`)
        return this.channel.send(Command.stopCollectDiagnostics, {});
    }

    public async viewFile(filePath: string): Promise<void> {
        await this.connectPromise;
        console.log(`********* <VIEW_FILE: ${filePath} /> *********`)
        return this.channel.send(Command.viewFile, { filePath });
    }

    public async getScreenshot(thumbnail: boolean): Promise<ImageInfo> {
        await this.connectPromise;
        console.log(`********* <GET_SCREENSHOT /> *********`)
        return this.channel.send(Command.getScreenshot, { thumbnail });
    }

    public async selectFiles(): Promise<FileSelection> {
        await this.connectPromise;
        console.log(`********* <SELECT_FILES /> *********`)
        return this.channel.send(Command.selectFiles, {});
    }

    public async startUploadingFiles(config: FileUploadConfig, screenshot?: ScreenshotCropConfig): Promise<void> {
        await this.connectPromise;
        console.log(`********* <START_UPLOADING_FILES> *********`)
        console.log({ config, screenshot });
        console.log(`********* </START_UPLOADING_FILES> *********`)
        return this.channel.send(Command.startUploadingFiles, { config, screenshot });
    }

    public async reserveNewTrackingId(): Promise<TrackingIdInfo> {
        await this.connectPromise;
        console.log(`********* <RESERVE_NEW_TRACKING_ID /> *********`)
        return this.channel.send(Command.reserveNewTrackingId, {});
    }

    public async chooseAccount(vsid: string): Promise<void> {
        await this.connectPromise;
        console.log(`********* <CHOOSE_ACCOUNT: ${vsid} /> *********`)
        return this.channel.send(Command.chooseAccount, { vsid });
    }
}

export interface ConnectionInfo {
    port: string;
    fsid: string;
}
